// Vendored from @tiptap/extension-mathematics v2. v3 stores a formula as one node instead of plain `\(...\)` text - better, but it cannot read our stored text, so adopting it needs a content migration plus Word-import, PDF-export and Changes-view changes. Separate ticket.
// Nearly all of this is v2's code unchanged; our only addition skips re-rendering a formula that has not changed.
// Decoration-only: nothing here reaches stored HTML.
import { Extension, getChangedRanges } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import katex from 'katex';
import type { KatexOptions } from 'katex';

export type TMathematicsOptions = {
  regex: RegExp;
  katexOptions?: KatexOptions;
  shouldRender: (state: EditorState, pos: number, node: PMNode) => boolean;
};

type TPluginState = {
  decorations: DecorationSet | undefined;
  isEditable: boolean | undefined;
};

const getAffectedRange = (
  newState: EditorState,
  previousPluginState: TPluginState,
  isEditable: boolean,
  tr: Transaction,
  state: EditorState
) => {
  const docSize = newState.doc.nodeSize - 2;
  let minFrom = 0;
  let maxTo = docSize;

  if (previousPluginState.isEditable !== isEditable) {
    minFrom = 0;
    maxTo = docSize;
  } else if (tr.docChanged) {
    minFrom = docSize;
    maxTo = 0;
    getChangedRanges(tr).forEach((range) => {
      minFrom = Math.min(minFrom, range.newRange.from - 1, range.oldRange.from - 1);
      maxTo = Math.max(maxTo, range.newRange.to + 1, range.oldRange.to + 1);
    });
  } else if (tr.selectionSet) {
    const { $from, $to } = state.selection;
    const { $from: $newFrom, $to: $newTo } = newState.selection;
    minFrom = Math.min($from.depth === 0 ? 0 : $from.before(), $newFrom.depth === 0 ? 0 : $newFrom.before());
    maxTo = Math.max($to.depth === 0 ? maxTo : $to.after(), $newTo.depth === 0 ? maxTo : $newTo.after());
  }

  return { minFrom: Math.max(minFrom, 0), maxTo: Math.min(maxTo, docSize) };
};

export const MathematicsPlugin = (options: TMathematicsOptions & { editor: Editor }) => {
  const { regex, katexOptions = {}, editor, shouldRender } = options;

  return new Plugin<TPluginState>({
    key: new PluginKey('mathematics'),
    state: {
      init() {
        return { decorations: undefined, isEditable: undefined };
      },
      apply(tr, previousPluginState, state, newState) {
        if (!tr.docChanged && !tr.selectionSet && previousPluginState.decorations) {
          return previousPluginState;
        }

        const nextDecorationSet = (previousPluginState.decorations || DecorationSet.empty).map(tr.mapping, tr.doc);
        const { selection } = newState;
        const isEditable = editor.isEditable;
        const decorationsToAdd: Decoration[] = [];
        const { minFrom, maxTo } = getAffectedRange(newState, previousPluginState, isEditable, tr, state);
        const decorationsToRemove = nextDecorationSet.find(minFrom, maxTo);
        // A formula's widget sits at its start, so editing only its closing delimiter may not touch the widget itself.
        const affectedFormulaStarts = new Set(
          decorationsToRemove
            .filter((decoration) => decoration.from < decoration.to)
            .map((decoration) => decoration.from)
        );
        decorationsToRemove.push(
          ...nextDecorationSet
            .find()
            .filter((decoration) => decoration.from === decoration.to && affectedFormulaStarts.has(decoration.from))
        );
        const retainedDecorations = nextDecorationSet.remove(decorationsToRemove);

        newState.doc.nodesBetween(minFrom, maxTo, (node, pos) => {
          if (!node.isText || !node.text || !shouldRender(newState, pos, node)) return;

          let match: RegExpExecArray | null;
          // eslint-disable-next-line no-cond-assign
          while ((match = regex.exec(node.text))) {
            const from = pos + match.index;
            const to = from + match[0].length;
            const content = match.slice(1).find(Boolean);
            if (!content) continue;

            const selectionSize = selection.from - selection.to;
            const anchorIsInside = selection.anchor >= from && selection.anchor <= to;
            const rangeIsInside = selection.from >= from && selection.to <= to;
            const isEditing = (selectionSize === 0 && anchorIsInside) || rangeIsInside;

            const alreadyPresent = retainedDecorations.find(
              from,
              to,
              (deco) =>
                isEditing === deco.isEditing &&
                content === deco.content &&
                isEditable === deco.isEditable &&
                katexOptions === deco.katexOptions
            ).length;
            if (alreadyPresent) continue;

            decorationsToAdd.push(
              Decoration.inline(
                from,
                to,
                {
                  class:
                    isEditing && isEditable
                      ? 'Tiptap-mathematics-editor'
                      : 'Tiptap-mathematics-editor Tiptap-mathematics-editor--hidden',
                  style:
                    !isEditing || !isEditable
                      ? 'display: inline-block; height: 0; opacity: 0; overflow: hidden; position: absolute; width: 0;'
                      : undefined,
                },
                { content, isEditable, isEditing, katexOptions }
              )
            );

            if (!isEditable || !isEditing) {
              decorationsToAdd.push(
                Decoration.widget(
                  from,
                  () => {
                    const element = document.createElement('span');
                    element.classList.add('Tiptap-mathematics-render');
                    if (isEditable) element.classList.add('Tiptap-mathematics-render--editable');
                    try {
                      katex.render(content, element, katexOptions);
                    } catch {
                      // textContent, not innerHTML: the raw source must never become markup.
                      element.textContent = content;
                    }
                    return element;
                  },
                  { content, isEditable, isEditing, katexOptions }
                )
              );
            }
          }
        });

        return {
          decorations: retainedDecorations.add(tr.doc, decorationsToAdd),
          isEditable,
        };
      },
    },
    props: {
      decorations(state) {
        return this.getState(state)?.decorations ?? DecorationSet.empty;
      },
    },
  });
};

export const defaultShouldRender = (state: EditorState, pos: number) => {
  const $pos = state.doc.resolve(pos);
  return $pos.parent.type.name !== 'codeBlock';
};

export const Mathematics = Extension.create<TMathematicsOptions>({
  name: 'Mathematics',

  addOptions() {
    return {
      // eslint-disable-next-line no-useless-escape
      regex: /\$([^\$]*)\$/gi,
      katexOptions: undefined,
      shouldRender: defaultShouldRender,
    };
  },

  addProseMirrorPlugins() {
    return [MathematicsPlugin({ ...this.options, editor: this.editor })];
  },
});

export default Mathematics;
